import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Car, Tv, CheckCircle2, Loader2, ArrowLeft, Trash2, PhoneCall, Users, Bus, QrCode } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { QrScanner } from "@/components/QrScanner";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const BG_STYLE = {
  background:
    "radial-gradient(1200px 600px at 15% 10%, rgba(37,99,235,0.18), transparent 60%)," +
    "radial-gradient(900px 500px at 95% 90%, rgba(14,165,233,0.18), transparent 55%)," +
    "linear-gradient(135deg, #f8fafc 0%, #eef2ff 55%, #e0f2fe 100%)",
};

export default function DismissalPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [number, setNumber] = useState("");
  const [calling, setCalling] = useState(false);
  const [queue, setQueue] = useState([]);
  const [buses, setBuses] = useState([]);
  const [busCalls, setBusCalls] = useState([]);
  const [scanOpen, setScanOpen] = useState(false);
  const inputRef = useRef(null);

  const allowed = user && (user.role === "admin" || user.role === "corporate_admin" || user.can_call_pickup);

  const loadQueue = useCallback(async () => {
    try {
      const [q, b, bc] = await Promise.all([
        api.get("/dismissal/queue"),
        api.get("/buses"),
        api.get("/dismissal/buses"),
      ]);
      setQueue(q.data);
      setBuses(b.data);
      setBusCalls(bc.data);
    } catch { /* polling */ }
  }, []);

  useEffect(() => {
    if (!allowed) return;
    loadQueue();
    const iv = setInterval(loadQueue, 4000);
    return () => clearInterval(iv);
  }, [allowed, loadQueue]);

  const call = async (e) => {
    e?.preventDefault();
    const n = number.trim();
    if (!n) return;
    setCalling(true);
    try {
      const { data } = await api.post("/dismissal/call", { car_number: n });
      toast.success(`${data.student_name} called to pickup`);
      setNumber("");
      loadQueue();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Call failed");
    } finally {
      setCalling(false);
      inputRef.current?.focus();
    }
  };

  const handleScan = useCallback(async (code) => {
    const raw = code.trim();
    const n = (raw.includes("CAR:") ? raw.split("CAR:", 2)[1] : raw).trim();
    if (!n || !/^[\w-]{1,20}$/.test(n)) {
      toast.error("Not a car tag QR code");
      return null;
    }
    try {
      const { data } = await api.post("/dismissal/call", { car_number: n });
      loadQueue();
      return { label: data.student_name, sub: `Car #${n} · called to pickup`, tone: "green" };
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Call failed";
      if (err.response?.status === 409) return { label: msg.replace(" is already on the board.", ""), sub: "Already on the board", tone: "amber" };
      toast.error(msg);
      return null;
    }
  }, [loadQueue]);

  const done = async (c) => {
    try {
      await api.post(`/dismissal/queue/${c.id}/done`);
      setQueue((q) => q.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const clearAll = async () => {
    if (!window.confirm("Clear the whole board?")) return;
    try {
      await api.delete("/dismissal/queue");
      setQueue([]);
      toast.success("Board cleared");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const announceBus = async (b) => {
    try {
      await api.post(`/dismissal/bus-call/${b.id}`);
      toast.success(`Bus ${b.number} announced on the board`);
      loadQueue();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  const busDone = async (call) => {
    try {
      await api.post(`/dismissal/buses/${call.id}/done`);
      setBusCalls((q) => q.filter((x) => x.id !== call.id));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    }
  };

  if (user && !allowed) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={BG_STYLE}>
        <div className="kiosk-rise text-center max-w-sm rounded-3xl border border-white/60 bg-white/85 backdrop-blur-2xl p-10 shadow-[0_25px_60px_-15px_rgba(15,23,42,0.25)]" data-testid="dismissal-denied">
          <Car className="h-10 w-10 text-primary/40 mx-auto mb-3" />
          <div className="font-display text-xl font-bold tracking-tight text-slate-900">Dismissal caller access needed</div>
          <p className="text-sm text-slate-600 mt-2">Ask your admin to enable "Dismissal caller" for your account on the Employees page.</p>
          <button onClick={() => nav(-1)} className="mt-5 text-sm font-medium text-primary hover:underline">Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen overflow-hidden" style={BG_STYLE} data-testid="dismissal-page">
      <div className="relative z-10 flex min-h-screen flex-col">
        <header className="flex flex-wrap items-center justify-between gap-3 px-6 md:px-12 py-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => nav(user?.role === "teacher" ? "/teacher" : "/admin")}
              data-testid="dismissal-back"
              className="flex h-10 w-10 items-center justify-center rounded-full bg-white/70 border border-white/60 text-slate-500 hover:text-slate-900 hover:bg-white transition backdrop-blur-xl shadow-sm"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div className="flex items-center gap-3 rounded-full bg-white/80 px-5 py-2.5 backdrop-blur-xl border border-white/60 shadow-sm">
              <Car className="h-5 w-5 text-primary" />
              <span className="font-display font-semibold tracking-tight text-slate-900">Car Rider Dismissal</span>
            </div>
          </div>
          <a
            href="/dismissal/board"
            target="_blank"
            rel="noreferrer"
            data-testid="open-board-btn"
            className="flex items-center gap-2 rounded-full bg-white/80 px-5 py-2.5 backdrop-blur-xl border border-white/60 shadow-sm text-sm font-medium text-slate-700 hover:text-slate-900 hover:bg-white transition"
          >
            <Tv className="h-4 w-4 text-primary" /> Open TV board
          </a>
        </header>

        <main className="flex-1 flex items-start justify-center px-6 pb-12">
          <div className="kiosk-rise w-full max-w-2xl space-y-6">
            <div className="rounded-3xl border border-white/60 bg-white/85 backdrop-blur-2xl p-8 md:p-10 shadow-[0_25px_60px_-15px_rgba(15,23,42,0.25)]">
              <div className="flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-primary mb-3">
                <PhoneCall className="h-4 w-4" /> Call a car number
              </div>
              <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight text-slate-900 mb-6">
                Who's here for pickup?
              </h1>
              <form onSubmit={call} className="flex flex-col sm:flex-row gap-3">
                <input
                  ref={inputRef}
                  autoFocus
                  inputMode="numeric"
                  size={1}
                  value={number}
                  onChange={(e) => setNumber(e.target.value)}
                  data-testid="dismissal-number-input"
                  placeholder="e.g. 42"
                  className="flex-1 min-w-0 w-full h-16 sm:h-20 rounded-2xl border-2 border-slate-200 bg-white px-6 text-3xl sm:text-4xl font-bold tracking-widest text-slate-900 focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none transition"
                />
                <button
                  type="submit"
                  disabled={calling || !number.trim()}
                  data-testid="dismissal-call-btn"
                  className="h-16 sm:h-20 w-full sm:w-auto justify-center rounded-2xl bg-primary px-8 md:px-10 text-xl font-semibold text-white inline-flex items-center gap-2.5 hover:shadow-lg hover:shadow-primary/25 active:scale-[0.98] disabled:opacity-50 transition"
                >
                  {calling ? <Loader2 className="h-6 w-6 animate-spin" /> : <PhoneCall className="h-6 w-6" />} Call
                </button>
              </form>
              <button
                type="button"
                onClick={() => setScanOpen(true)}
                data-testid="dismissal-scan-btn"
                className="mt-3 w-full h-14 rounded-2xl border-2 border-dashed border-slate-300 bg-white/60 text-slate-700 font-semibold inline-flex items-center justify-center gap-2.5 hover:border-primary hover:text-primary active:scale-[0.99] transition"
              >
                <QrCode className="h-5 w-5" /> Scan car tag QR
              </button>
            </div>

            <section className="rounded-3xl border border-white/60 bg-white/85 backdrop-blur-2xl shadow-[0_25px_60px_-15px_rgba(15,23,42,0.25)] overflow-hidden">
              <div className="px-6 md:px-8 py-4 border-b border-slate-200/70 flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <Users className="h-4 w-4 text-slate-400" />
                  <span className="font-display font-semibold text-slate-900">On the board</span>
                  <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary/10 px-1.5 text-[11px] font-bold text-primary">{queue.length}</span>
                </div>
                {queue.length > 0 && (
                  <button onClick={clearAll} data-testid="dismissal-clear-btn" className="inline-flex items-center gap-1.5 text-xs font-medium text-destructive hover:underline">
                    <Trash2 className="h-3.5 w-3.5" /> Clear board
                  </button>
                )}
              </div>
              {queue.length === 0 && (
                <div className="p-10 text-center text-sm text-slate-500" data-testid="dismissal-queue-empty">
                  No students on the board yet — call a number above.
                </div>
              )}
              {queue.map((c) => (
                <div key={c.id} className="px-6 md:px-8 py-3.5 border-b border-slate-100 flex items-center gap-4" data-testid={`dismissal-call-${c.id}`}>
                  <div className="h-12 w-16 shrink-0 rounded-xl bg-primary/10 text-primary flex items-center justify-center font-display text-xl font-bold">{c.car_number}</div>
                  <div className="min-w-0 flex-1">
                    <div className="font-display font-semibold tracking-tight text-slate-900 truncate">{c.student_name}</div>
                    <div className="text-xs text-slate-500">{c.grade ? `${c.grade} · ` : ""}called {new Date(c.called_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} by {c.called_by}</div>
                  </div>
                  <button
                    onClick={() => done(c)}
                    data-testid={`dismissal-done-${c.id}`}
                    className="inline-flex items-center gap-1.5 rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-700 active:scale-[0.98] transition"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Picked up
                  </button>
                </div>
              ))}
            </section>

            {buses.length > 0 && (
              <section className="rounded-3xl border border-white/60 bg-white/85 backdrop-blur-2xl shadow-[0_25px_60px_-15px_rgba(15,23,42,0.25)] overflow-hidden" data-testid="dismissal-buses-section">
                <div className="px-6 md:px-8 py-4 border-b border-slate-200/70 flex items-center gap-2 text-sm">
                  <Bus className="h-4 w-4 text-slate-400" />
                  <span className="font-display font-semibold text-slate-900">Buses</span>
                  <span className="text-xs text-slate-400">announce when a bus is ready for boarding</span>
                </div>
                <div className="p-5 flex flex-wrap gap-3">
                  {buses.map((b) => {
                    const active = busCalls.find((c) => c.bus_id === b.id);
                    return active ? (
                      <button
                        key={b.id}
                        onClick={() => busDone(active)}
                        data-testid={`bus-done-${b.id}`}
                        title="On the board — tap to remove"
                        className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 text-sm font-bold text-slate-900 hover:bg-amber-300 active:scale-[0.98] transition"
                      >
                        <Bus className="h-4 w-4" /> Bus {b.number} · boarding <CheckCircle2 className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        key={b.id}
                        onClick={() => announceBus(b)}
                        data-testid={`bus-announce-${b.id}`}
                        className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 active:scale-[0.98] transition"
                      >
                        <Bus className="h-4 w-4 text-primary" /> Announce bus {b.number}
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
          </div>
        </main>
      </div>
      {scanOpen && (
        <QrScanner
          title="Scan car tag"
          hint="Point the camera at the QR code on the family's car tag — the number is called instantly."
          onScan={handleScan}
          onClose={() => setScanOpen(false)}
        />
      )}
    </div>
  );
}
